/**
 * Where Lykke can take a client: the guide's page map (guide/pageMap.ts, the same gates as the sidebar), sent to the
 * backend each turn, and the path for a page id + params + the item to focus on it. Pure TypeScript.
 *
 * Focus: content approval opens one draft (?draft_product_id=&draft_version_id=, the same deep link the back office
 * uses); creator suggestions open one creator's profile (?creatorProfile=). `lykke-campaign` is Lykke's own page for
 * that campaign's conversation (/lykke/:campaignId); without a campaign, /lykke.
 */
import { mapFor, pathFor, type Viewer } from '../guide/pageMap';

/**
 * The page map for Lykke. The Deck studio also builds: opened with `params.prompt` it makes the deck at once (the
 * app passes the person's own words to its deck agent, LykkeContext), so her map says how to ask for that.
 */
export const lykkePagesFor = (v: Viewer) => mapFor(v).map((p) => (p.id === 'reports'
  ? { ...p, does: `${p.does} To make a new deck, open it with params.prompt = what the deck is about, in the person's words (it starts building at once).` }
  : p));

const ID = /^[A-Za-z0-9_-]{3,64}$/;

/** The app path for a Lykke page id, or null when the page cannot be opened with these params. */
export function lykkePath(page: string | undefined, params: Record<string, string> = {}, focus: Record<string, string> = {}): string | null {
  if (!page) return null;
  const campaignId = params.campaignId && ID.test(params.campaignId) ? params.campaignId : '';
  if (page === 'lykke-campaign') return campaignId ? `/lykke/${campaignId}` : '/lykke';
  let base = pathFor(page, params);
  if (!base) return null;
  // A campaign page without its tab segment reopens the last tab the person saw: always name it.
  if (page === 'campaign-overview' && campaignId && base === `/campaigns/${campaignId}`) base = `/campaigns/${campaignId}/overview`;
  const q = new URLSearchParams(base.includes('?') ? base.slice(base.indexOf('?') + 1) : '');
  const path = base.includes('?') ? base.slice(0, base.indexOf('?')) : base;
  if (page === 'campaign-contentApproval' && focus.draftProductId && ID.test(focus.draftProductId)) {
    q.set('draft_product_id', focus.draftProductId);
    if (focus.draftVersionId && ID.test(focus.draftVersionId)) q.set('draft_version_id', focus.draftVersionId);
  }
  if (page === 'campaign-profiles' && focus.creatorProfile && ID.test(focus.creatorProfile)) q.set('creatorProfile', focus.creatorProfile);
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
}
